import React, { useState, useEffect, useContext } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  isFuture,
} from "date-fns";
import { LoginContext } from "../contexts/logincontext";
import axios from "axios";
import { toast } from "react-toastify";

const CustomerAttendance = () => {
  const [attendance, setAttendance] = useState({});
  const { loggedInUser } = useContext(LoginContext);
  const [showModal, setShowModal] = useState(false);
  const [clickedDate, setClickedDate] = useState(null);
  const [isAttendanceGiven, setIsAttendanceGiven] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch attendance from the backend
  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `https://klimkaara-backend-pgnq.onrender.com/attendance/getattendance/${loggedInUser?.clientId}`
        );
        if (response.status === 200) {
          const formattedAttendance = response.data.attendance.reduce(
            (acc, date) => {
              acc[format(new Date(date), "yyyy-MM-dd")] = "confirmed";
              return acc;
            },
            {}
          );
          setAttendance(formattedAttendance);
        } else {
          console.error("Error fetching attendance:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };

    if (loggedInUser?.clientId) {
      fetchAttendance();
      setLoading(false);
    }
  }, [loggedInUser?.clientId]);

  // Get the current date
  const today = new Date();

  // Get the start and end of the current month
  const startDate = startOfMonth(today);
  const endDate = endOfMonth(today);

  // Generate an array of all dates in the current month
  const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });

  // Function to get the class for each date
  const getDateClass = (date) => {
    const dateString = format(date, "yyyy-MM-dd");

    if (attendance[dateString] === "confirmed")
      return "bg-yellow-400 opacity-50 cursor-not-allowed";
    if (isToday(date)) return "bg-yellow-400 text-black";
    if (isFuture(date)) return "bg-gray-600 text-gray-400 cursor-not-allowed";
    if (!attendance[dateString])
      return "bg-red-400 cursor-not-allowed opacity-50";
    return "bg-yellow-200 text-black cursor-not-allowed opacity-50";
  };

  // Handle date click (only allow clicking on today)
  const handleDateClick = (date) => {
    const dateString = format(date, "yyyy-MM-dd");
    if (isToday(date)) {
      setClickedDate(date);
      setIsAttendanceGiven(attendance[dateString] === "confirmed");
      setShowModal(true);
    }
  };

  // Mark attendance API call
  const markAttendance = async (latitude, longitude) => {
    const dateString = format(clickedDate, "yyyy-MM-dd");
    console.log(loggedInUser);
    console.log(loggedInUser?.clientId, dateString,latitude,longitude);
    try {
      const response = await axios.post(
        "https://klimkaara-backend-pgnq.onrender.com/attendance/markAttendance",
        {
          clientId: loggedInUser?.clientId,
          date: dateString,
          latitude,
          longitude,
        }
      );

      if (response.status === 200) {
        setAttendance((prev) => ({ ...prev, [dateString]: "confirmed" }));
        setShowModal(false);
        toast.success("Attendance marked successfully");
      } else {
        alert("Error marking attendance: " + response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      console.error("Error marking attendance:", error);
    }
  };

  const getCoordinates = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log("User Latitude:", latitude);
          console.log("User Longitude:", longitude);

          // Now you can send these coordinates to the backend for validation
          markAttendance(latitude, longitude);
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Location access denied.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-8">
          Attendance Calendar
        </h2>

        {/* Legend */}
        <div className="md:flex md:justify-center md:space-x-6 mb-8">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-400 mr-2"></div>
            <span>Absent Days</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-400 mr-2"></div>
            <span>Current Day</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-200 mr-2"></div>
            <span>Past Days</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-600 mr-2"></div>
            <span>Future Dates (Disabled)</span>
          </div>
        </div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid md:grid-cols-7 sm:grid-cols-4 grid-cols-3 gap-2 text-center">
            {daysInMonth.map((date, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg ${getDateClass(date)} ${
                  isToday(date) ? "cursor-pointer" : ""
                }`}
                onClick={() => handleDateClick(date)}
              >
                {format(date, "d")}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg text-center text-black">
            {isAttendanceGiven ? (
              <>
                <h2 className="text-xl mb-4">Attendance Already Given</h2>
                <p>You've already marked today's attendance.</p>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-400 rounded-full font-bold"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <h2 className="text-xl mb-4">Confirm Attendance</h2>
                <p>Do you want to mark today's attendance?</p>
                <div className="mt-6 space-x-4">
                  <button
                    onClick={getCoordinates}
                    className="px-6 py-2 bg-yellow-400 rounded-full text-black font-bold"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-400 rounded-full font-bold"
                  >
                    No
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAttendance;

